/* iter55u — Extracted from HelpManualPanel.jsx.
 *
 * Owns the typed-block editor row (paragraph / list / step / tip / code)
 * plus the tiny helpers the parent panel used to inline. Kept
 * dependency-free so the parent stays a coordinator.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ArrowUp, ArrowDown, Trash2 } from 'lucide-react';

export const BLOCK_KINDS = [
  { value: 'para', label: 'Paragraph' },
  { value: 'list', label: 'Bullet list' },
  { value: 'step', label: 'Numbered step' },
  { value: 'tip',  label: 'Tip (highlighted)' },
  { value: 'code', label: 'Code / monospace' },
];

// iter-40 — Small relative-time helper. Shared between the section
// list (per-row "updated 2h ago") and the broadcast indicator.
export function fmtRelative(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hrs = Math.round(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

// iter-53h — Stable per-block ids so reorder / delete / add don't desync
// React's mount state with the underlying blocks array. `_lid` is a
// transient client-side marker; stripped before PUT.
let _LID_COUNTER = 0;
export function _nextLid() {
  _LID_COUNTER += 1;
  return `b-${Date.now().toString(36)}-${_LID_COUNTER}`;
}

export function newBlock(kind = 'para') {
  if (kind === 'list') return { kind, items: ['First item'], _lid: _nextLid() };
  if (kind === 'step') return { kind, n: 1, text: 'Describe the step.', _lid: _nextLid() };
  return { kind, text: 'New content.', _lid: _nextLid() };
}

export function BlockEditor({ block, onChange, onRemove, onMoveUp, onMoveDown }) {
  return (
    <div className="rounded-md border border-border bg-card p-2.5 space-y-2" data-testid={`help-block-${block.kind}`}>
      <div className="flex items-center justify-between gap-2 text-[11px]">
        <div className="flex items-center gap-1.5">
          <select
            className="h-7 rounded-sm border border-border bg-background px-1.5 text-[11.5px]"
            value={block.kind}
            onChange={(e) => onChange({
              ...newBlock(e.target.value),
              ...(block.kind === e.target.value ? block : {}),
              kind: e.target.value,
            })}
          >
            {BLOCK_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
          </select>
          {block.kind === 'step' && (
            <Input
              type="number"
              className="h-7 w-16 text-[11.5px]"
              min={1}
              value={block.n || 1}
              onChange={(e) => onChange({ ...block, n: parseInt(e.target.value || '1', 10) })}
            />
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMoveUp}><ArrowUp className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={onMoveDown}><ArrowDown className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={onRemove}><Trash2 className="h-3 w-3" /></Button>
        </div>
      </div>
      {block.kind === 'list' ? (
        <Textarea
          rows={4}
          value={(block.items || []).join('\n')}
          onChange={(e) => onChange({
            ...block,
            items: e.target.value.split('\n').map(x => x.trimEnd()).filter(Boolean),
          })}
          placeholder="One bullet per line"
        />
      ) : (
        <Textarea
          rows={block.kind === 'tip' ? 2 : 3}
          value={block.text || ''}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          placeholder="Body text"
        />
      )}
    </div>
  );
}
